import { useHttp, useTranslate } from '@/shared/lib'
import { useNotification } from '@/shared/ui'
import { useMutation } from '@tanstack/vue-query'
import type { ObjectInventoryField } from '@/entities/Inventory/ObjectTypes'
import { buildPassportRuntimeFieldPayload, type RuntimeFieldValueMap } from './runtimeFieldBindings'

export type ObjectPassportSavePayload = {
  id: number | string
  name?: string
  is_projecting?: boolean
  /** Canonical key expected by some backends; mirror of `connection_object_list_id` when both are sent. */
  connection_object_id?: number | null
  connection_object_list_id?: number | null
  fields: { id: string; value: string }[]
  files?: unknown
  comment?: string
  relation_links?: unknown[]
  /** When true and workflow is disabled, skip version creation and update in-place. */
  is_original?: boolean
  [key: string]: unknown
}

type ObjectPassportSaveInput = Omit<ObjectPassportSavePayload, 'fields'> & {
  fields: RuntimeFieldValueMap
}

export function buildObjectPassportSavePayload(
  data: ObjectPassportSaveInput,
  fieldMetadata: ObjectInventoryField[] | null | undefined,
): ObjectPassportSavePayload {
  return {
    ...data,
    fields: buildPassportRuntimeFieldPayload(data.fields, fieldMetadata),
  } as ObjectPassportSavePayload
}

/**
 * Функция сохранения данных инвентаризационного объекта для Ленсвета
 */
export const useSendObjectList = () => {
  const http = useHttp()

  const { addError, addSuccess } = useNotification()
  const { trs } = useTranslate()

  const mutation = useMutation({
    mutationFn: (data: ObjectPassportSavePayload) => {
      return http.put(`/inventory/objects/passport/${data.id}`, data)
    },
  })

  const send = async (data: ObjectPassportSavePayload, verDate?: string) => {
    try {
      const payload = { ...data }
      if (verDate) payload.ver_date = verDate
      await mutation.mutateAsync(payload)

      addSuccess(trs('itf.saved'))
      return true
    } catch (error: any) {
      addError(trs(error?.response?.data?.data?.msg || 'request.unknown_error'))
      return false
    }
  }

  return {
    ...mutation,

    send,
  }
}
